/* eslint-disable react/no-multi-comp */
import React, { useEffect, useState } from 'react'
import { useLocation, matchPath, useHistory } from 'react-router'
import clsx from 'clsx'
import PropTypes from 'prop-types'
import { useSelector, useDispatch } from 'react-redux'
import { makeStyles } from '@material-ui/styles'
import {
  Drawer,
  Divider,
  List,
  ListSubheader,
  Hidden,
  colors,
  TextField,
  Typography
} from '@material-ui/core'
import NavItem from 'src/components/NavItem'
import { useConfig } from './navConfig'
import isSuperAdmin from 'src/utils/isSuperAdmin'
import { setCompany, setHeadCompany, setMarketplaces } from '../../actions'
import { useTranslation } from 'react-i18next'
import api from '../../utils/api'
import { getElectronicSignatureModule } from 'src/utils/electronicSignatureApi'
import {
  confirming, cooperativeProgram,
  creditCard,
  creditCardOrderToCash, crossBorder,
  dynamicDiscounting,
  evolvedPayByLink, manualInputOutput, reverseFactoring
} from '../../utils/typeOfPrograms'
import CryptoJS from 'crypto-js'
import { setCashPlanningHeaders } from 'src/utils/cashPlanningApi'


function calculateHash (string) {
  return CryptoJS.SHA256(string).toString()
}

const PLATFORM_MENU_VALUE = '__platformMenu__'
const validCompanyRolePattern = /(Buyer|Supplier|Creditor|OrderToCash|FunderPartner|CashPlanning|RFDebtor)/

const getCompanyInitials = (name = '') => String(name || '')
  .split(/\s+/)
  .filter(Boolean)
  .slice(0, 2)
  .map(part => part.charAt(0))
  .join('')
  .toUpperCase() || 'PA'

const useStyles = makeStyles((theme) => ({
  root: {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    transition: 'width .2s ease'
  },
  mobileDrawer: {
    width: 'min(280px, 82vw)'
  },
  desktopDrawer: ({ topOffset = 64, collapsed }) => ({
    width: collapsed ? 68 : '14.5vw',
    top: topOffset,
    height: `calc(100% - ${topOffset}px)`,
    backgroundColor: '#fff',
    borderRight: '1px solid #e6eaed',
    transition: 'width .2s ease'
  }),
  navigation: {
    overflow: 'auto',
    padding: theme.spacing(0, 1.25),
    margin: theme.spacing(0),
    flexGrow: 1
  },
  companyDivider: {
    margin: theme.spacing(2, 0)
  },
  profile: {
    position: 'relative',
    padding: theme.spacing(1.5, 1.25, 0)
  },
  companyLabel: {
    display: 'block',
    margin: theme.spacing(0, 0, 0.75, 1),
    color: '#718591'
  },
  companyControl: {
    position: 'relative'
  },
  companyAvatar: {
    position: 'absolute',
    zIndex: 1,
    top: '50%',
    left: 10,
    transform: 'translateY(-50%)',
    display: 'flex',
    width: 25,
    height: 25,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
    backgroundColor: '#fff0f1',
    color: theme.palette.primary.main,
    pointerEvents: 'none'
  },
  companySelect: {
    width: '100%',
    '& .MuiOutlinedInput-root': {
      height: 45,
      borderRadius: 8,
      backgroundColor: '#fff',
      transition: 'background .15s ease, border-color .15s ease',
      '&:hover': {
        backgroundColor: '#fbfcfd'
      },
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: '#cfd8de'
      },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: theme.palette.primary.main,
        borderWidth: 1
      }
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: '#dde4e8'
    },
    '& .MuiSelect-select, & .MuiNativeSelect-select': {
      padding: theme.spacing(1.25, 4, 1.25, 5.25),
      color: '#42535f',
      fontSize: 12,
      fontWeight: 600
    }
  },
  collapsedProfile: {
    padding: theme.spacing(1.5, 0.75, 0),
    '& $companyControl': {
      display: 'flex',
      justifyContent: 'center'
    },
    '& $companySelect': {
      display: 'none'
    },
    '& $companyAvatar': {
      position: 'static',
      transform: 'none',
      width: 40,
      height: 40,
      borderRadius: 8
    }
  },
  subheader: {
    padding: theme.spacing(3, 1, 0.75),
    color: '#718591',
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: '0.06em',
    lineHeight: 1.2,
    textTransform: 'uppercase'
  },
  collapsedSubheader: {
    display: 'none'
  },
  badge: {
    boxShadow: `0 0 0 2px ${theme.palette.background.paper}`
  },
  badgeDot: {
    height: 9,
    minWidth: 9
  },
  onlineBadge: {
    backgroundColor: colors.green[600]
  },
  awayBadge: {
    backgroundColor: colors.orange[600]
  },
  busyBadge: {
    backgroundColor: colors.red[600]
  },
  offlineBadge: {
    backgroundColor: colors.grey[300]
  },
  avatar: {
    backgroundColor: theme.palette.primary.main,
    cursor: 'pointer',
    width: 30,
    height: 30
  },
  details: {
    marginLeft: theme.spacing(2)
  },
  moreButton: {
    marginLeft: 'auto',
    color: colors.blueGrey[200]
  },
  rfDebtorSubheader: {
    position: 'relative',
    '&::after': {
      content: '""',
      position: 'absolute',
      bottom: 0,
      left: 0,
      width: '60%',
      height: '2px',
      backgroundColor: 'rgb(0, 135, 93)'
    }
  }
}))

// Funzione che renderizza gli elementi del menu laterale
function renderNavItems ({
  items, subheader, key, classes, sectionType, collapsed, onExpand, ...rest
}) {
  const isRFDebtorSubheader = sectionType === 'RFDebtorAdmin'

  return (
    items && items.length > 0
      ? <List key={key} disablePadding>
        {subheader && (
          <ListSubheader
            disableSticky
            className={clsx(
              classes.subheader,
              isRFDebtorSubheader && classes.rfDebtorSubheader,
              collapsed && classes.collapsedSubheader
            )}
          >
            {subheader}
          </ListSubheader>
        )}
        {items && items.reduce(
          (acc, item) => reduceChildRoutes({ acc, item, classes, collapsed, onExpand, ...rest }),
          []
        )}
      </List> : <span key={key} />
  )
}

// funzione che permette di renderizzare anche sotto-elementi nel menu laterale
function reduceChildRoutes ({
  acc, pathname, item, depth = 0, classes, collapsed, onExpand
}) {
  if (item.items) {
    const open = matchPath(pathname, {
      path: item.href,
      exact: false
    })

    acc.push(
      <NavItem
        depth={depth}
        icon={item.icon}
        key={`${item.href}-${item.label}-${depth}-${acc.length}`}
        label={item.label}
        open={Boolean(open)}
        title={item.title}
        collapsed={collapsed}
        onExpand={onExpand}
      >
        {renderNavItems({
          depth: depth + 1,
          pathname,
          items: item.items,
          classes: classes,
          collapsed,
          onExpand
        })}
      </NavItem>
    )
  } else {
    acc.push(
      <NavItem
        depth={depth}
        href={item.href}
        icon={item.icon}
        key={`${item.href}-${item.title}-${depth}-${acc.length}`}
        label={item.label}
        title={item.title}
        isExternalUrl={item.isExternalUrl}
        collapsed={collapsed}
        onExpand={onExpand}
      />
    )
  }

  return acc
}
const addPassivePrograms = (navConfig, currentCompany) => {
  const passiveCycleItems = navConfig.newConfiguration.passiveCycle
  let skipManualUploadSection = false
  if (currentCompany.passivePrograms) {
    for (const program of currentCompany.passivePrograms) {
      if (program === dynamicDiscounting) {
        passiveCycleItems.items.push(navConfig.ddItem)
      }
      if (program === creditCard) {
        passiveCycleItems.items.push(navConfig.creditCardItem)
      }
      if (program === confirming) {
        passiveCycleItems.items.push(navConfig.confirmingItem)
      }
      if (program === reverseFactoring) {
        passiveCycleItems.items.push(navConfig.reverseItem)
      }
      if (program === crossBorder) {
        passiveCycleItems.items.push(navConfig.crossBorderPaymentsSection)
      }
      if (program === cooperativeProgram) {
        skipManualUploadSection = true
        passiveCycleItems.items.push(navConfig.cooperativeProgramSection)
      }
      if (program === manualInputOutput) {
        skipManualUploadSection = true
        passiveCycleItems.items.push(navConfig.manualInputOutputSection)
      }
    }
    if (navConfig.electronicSignatureItem) {
      passiveCycleItems.items.push(navConfig.electronicSignatureItem)
    }
    passiveCycleItems.items.push(navConfig.handleSuppliers)
    if (!skipManualUploadSection) {
      passiveCycleItems.items.push(navConfig.uploadInvoicesItem)
    }
    if (skipManualUploadSection) {
      passiveCycleItems.items.push(navConfig.requestReportSection)
    }
    // passiveCycleItems.items.push(navConfig.approveInvoices)
  }
  return passiveCycleItems
}
function NavBar ({
  openMobile,
  openDesktop,
  collapsedDesktop,
  onToggleDesktop,
  onMobileClose,
  topOffset,
  className,
  ...rest
}) {
  const isCollapsed = Boolean(collapsedDesktop && !openMobile)
  const classes = useStyles({ topOffset, collapsed: isCollapsed })
  const location = useLocation()
  const dispatch = useDispatch()
  const history = useHistory()
  const session = useSelector((state) => state.session)
  const { t } = useTranslation('navBar')
  const [electronicSignatureEnabled, setElectronicSignatureEnabled] = useState(false)
  const [adminMenuMode, setAdminMenuMode] = useState('platform')

  // utilizzo i valori di tutte le aziende della azienda corrente con cui sto visualizzando la dashboard
  const { companies, currentCompany, user, headCompanies, currentHeadCompany } = session

  // Verifico se sto visualizzando come superAdmin
  const isSa = isSuperAdmin(session.user.email)

  // utilizzo Set per avere un array di companies uniche e non ripetute
  const getUniqueCompanies = (companies) => [...new Set(companies.map(company => company.uuid))].map(uuid => companies.find(currCompany => currCompany.uuid === uuid))
  const companyIdsWithValidRoles = new Set((user.role || [])
    .filter(permission => permission.companyId && validCompanyRolePattern.test(permission.role))
    .map(permission => permission.companyId))
  // Per gli utenti PA/admin mostro il selettore aziendale solo se le ACL hanno ruoli gestiti da displayRoleMenu.
  const validRoleCompanies = getUniqueCompanies(companies || [])
    .filter(company => company && company.uuid && companyIdsWithValidRoles.has(company.uuid))
  const hasValidCompanyRoles = validRoleCompanies.length > 0
  const selectedCompanyHasValidRole = currentCompany && currentCompany.uuid && companyIdsWithValidRoles.has(currentCompany.uuid)
  const isCompanyMenuMode = !isSa || (adminMenuMode === 'company' && selectedCompanyHasValidRole)
  const navConfig = useConfig({ electronicSignatureEnabled })
  const companyId = currentCompany && currentCompany.uuid

  useEffect(() => {
    let cancelled = false
    // La voce compare solo se il modulo dell'azienda in sessione è abilitato.
    if (!companyId || (isSa && !isCompanyMenuMode)) {
      setElectronicSignatureEnabled(false)
      return undefined
    }
    setElectronicSignatureEnabled(false)
    getElectronicSignatureModule(companyId)
      .then(data => {
        if (!cancelled) setElectronicSignatureEnabled(Boolean(data && data.enabled))
      })
      .catch(() => {
        if (!cancelled) setElectronicSignatureEnabled(false)
      })
    return () => { cancelled = true }
  }, [companyId, isSa, isCompanyMenuMode])

  let isCreditor = false
  let isOrderToCash = false
  let isCashPlanning = false
  // Funzione che mi permette di visualizzare il menu corretto per tutti i ruoli che un utente può avere
  const displayRoleMenu = () => {
    // Creo un array di ruoli dalle regole acl
    const permissions = user && user.role && user.role
      .filter(companyPermission => companyPermission.companyId === currentCompany.uuid)
      .map(permission => permission.role)

    // Includo divido tutti i permessi per categoria di utente
    const permissionsPerRole = permissions && permissions
      .reduce((obj, permission) => {
        if (permission.includes('Buyer')) {
          obj.buyer.push(permission)
        } else if (permission.includes('Supplier')) {
          obj.supplier.push(permission)
        } else if (permission.includes('CallCenter')) {
          obj.callcenter.push(permission)
        } else if (permission.includes('Creditor')) {
          obj.creditor.push(permission)
        } else if (permission.includes('OrderToCash')) {
          obj.orderToCash.push(permission)
        } else if (permission.includes('FunderPartner')) {
          obj.funder.push(permission)
        } else if (permission.includes('CashPlanning')) {
          obj.cashplanning.push(permission)
        } else if (permission.includes('RFDebtor')) {
          obj.rfDebtor.push(permission)
        }
        return obj
      }, { buyer: [], supplier: [], callcenter: [], funder: [], creditor: [], orderToCash: [], cashplanning: [], rfDebtor: [] })
    // controllo se sono Admin in per qualche categoria
    isCreditor = permissionsPerRole && permissionsPerRole.creditor.some(permission => permission.includes('Admin'))
    isOrderToCash = permissionsPerRole && permissionsPerRole.orderToCash.some(permission => permission.includes('Admin'))
    isCashPlanning = permissionsPerRole && permissionsPerRole.cashplanning.some(permission => permission.includes('Admin'))
    const isBuyerAdmin = permissionsPerRole && permissionsPerRole.buyer.some(permission => permission.includes('Admin'))
    const isFunderAdmin = permissionsPerRole && permissionsPerRole.funder.some(permission => permission.includes('Admin'))
    const isSupplierAdmin = permissionsPerRole && permissionsPerRole.supplier.some(permission => permission.includes('Admin'))
    const isBuyerManager = permissionsPerRole && permissionsPerRole.buyer.some(permission => permission.includes('Manager'))
    const isSupplierManager = permissionsPerRole && permissionsPerRole.supplier.some(permission => permission.includes('Manager'))
    const isBuyerOperator = permissionsPerRole && permissionsPerRole.buyer.some(permission => permission.includes('Operator'))
    const isSupplierOperator = permissionsPerRole && permissionsPerRole.supplier.some(permission => permission.includes('Operator'))
    const isRFDebtorUser = permissionsPerRole && permissionsPerRole.rfDebtor.length > 0
    // controllo se sono commerciale di callcenter
    const isCallcenterSalesperson = permissionsPerRole && permissionsPerRole.callcenter.some(permission => permission.includes('Salesperson'))
    // controllo se sono operatore di callcenter
    const isCallcenterOperator = permissionsPerRole && permissionsPerRole.callcenter.some(permission => permission.includes('Operator'))

    // In base ai ruoli compongo il menu con tutte le voci per ogni ruolo
    if (isFunderAdmin) {
      return [
        navConfig.funderPartner.admin
      ]
    } else if (isCallcenterSalesperson && isCallcenterOperator) {
      return [navConfig.callcenter.commercial, navConfig.callcenter.operator]
    } else if (isCallcenterOperator && !isCallcenterSalesperson) {
      return [navConfig.callcenter.operator]
    } else if (isCallcenterSalesperson && !isCallcenterOperator) {
      return [navConfig.callcenter.commercial]
    } else if (isRFDebtorUser) {
      return [
        navConfig.RFDebtorAdmin,
        navConfig.supplier.support
      ]
    } else if ((isBuyerAdmin && isSupplierAdmin) || (isBuyerManager && isSupplierManager) || (isBuyerOperator && isSupplierAdmin)) {
      if (currentCompany && currentCompany.hasMultiplePrograms) {
        const passiveCycleItems = addPassivePrograms(navConfig, currentCompany)
        const activeCycleItems = navConfig.newConfiguration.activeCycle
        if (currentCompany.activePrograms) {
          for (const program of currentCompany.activePrograms) {
            if (program === creditCardOrderToCash) {
              activeCycleItems.items = activeCycleItems.items
                .concat(navConfig.orderToCashSection.items)
            }
            if (program === evolvedPayByLink) {
              activeCycleItems.items.push(navConfig.evolvedPayByLink)
            }
          }
        }
        const allItems = [
          passiveCycleItems,
          navConfig.buyer.admin,
          activeCycleItems,
          navConfig.integrationsItems,
          navConfig.supplier.support
        ]
        if (currentCompany && currentCompany.isDebtor) {
          allItems.unshift(getOrderToCashItem(currentCompany))
        }
        return allItems
      }
      const allItems = [
        // navConfig.buyer.financial,
        // navConfig.buyer.technical,
        navConfig.supplier.supplierAndBuyer,
        // navConfig.supplier.invitation,
        navConfig.buyer.admin,
        navConfig.integrationsItems,
        navConfig.supplier.support
      ]
      if (currentCompany && currentCompany.isDebtor) {
        allItems.unshift(getOrderToCashItem(currentCompany))
      } else {
        allItems.unshift(navConfig.buyer.buyerAndSupplier)
      }
      return allItems
    } else if ((isBuyerAdmin && !isSupplierAdmin) || (isBuyerManager && !isSupplierManager)) {
      if (currentCompany && currentCompany.isDebtor) {
        return createOrderToCashDebtorNav(currentCompany)
      }

      if (currentCompany && currentCompany.hasMultiplePrograms) {
        let passiveCycleItems
        if (currentCompany.passivePrograms && currentCompany.passivePrograms.length > 0) {
          passiveCycleItems = addPassivePrograms(navConfig, currentCompany)
        } else {
          passiveCycleItems = navConfig.buyer.multiOptionBuyer
        }
        return [
          // navConfig.buyer.multiOptionBuyer,
          passiveCycleItems,
          navConfig.buyer.admin,
          navConfig.integrationsItems,
          navConfig.buyer.support
        ]
      }
      return [
        navConfig.buyer.common,
        navConfig.buyer.admin,
        navConfig.integrationsItems,
        navConfig.buyer.support
        // navConfig.buyer.financial
        // navConfig.buyer.technical
      ]
    } else if ((isSupplierAdmin && !isBuyerAdmin) || (isSupplierManager && !isBuyerManager)) {
      if (currentCompany && currentCompany.hasMultiplePrograms) {
        const activeCycleItems = navConfig.newConfiguration.activeCycle
        if (currentCompany.activePrograms) {
          for (const program of currentCompany.activePrograms) {
            if (program === creditCardOrderToCash) {
              activeCycleItems.items = activeCycleItems.items
                .concat(navConfig.orderToCashSection.items)
            }
            if (program === evolvedPayByLink) {
              activeCycleItems.items.push(navConfig.evolvedPayByLink)
            }
          }
        }
        return [
          activeCycleItems,
          navConfig.supplier.admin,
          navConfig.integrationsSupplierItems,
          navConfig.supplier.support,
          navConfig.supplier.financial,
          navConfig.supplier.technical
        ]
      }
      const common = navConfig.supplier.common
      if (isOrderToCash) {
        common.subheader = t('activeCycleBuyerLed')
      }
      return [
        common,
        navConfig.supplier.admin,
        navConfig.supplier.invitation,
        navConfig.integrationsSupplierItems,
        navConfig.supplier.support,
        navConfig.supplier.financial,
        navConfig.supplier.technical
      ]
    } else if (isSupplierOperator) {
      if (isBuyerOperator) {
        let passiveCycleItems
        if (currentCompany.passivePrograms && currentCompany.passivePrograms.length > 0) {
          passiveCycleItems = addPassivePrograms(navConfig, currentCompany)
        } else {
          passiveCycleItems = navConfig.buyer.multiOptionBuyer
        }
        return [
          passiveCycleItems,
          navConfig.supplier.supplierAndBuyer,
          navConfig.integrationsSupplierItems,
          navConfig.supplier.support
        ]
      } else {
        return [
          navConfig.supplier.common,
          navConfig.supplier.admin,
          navConfig.integrationsSupplierItems,
          navConfig.supplier.support
        ]
      }
    } else if (isBuyerOperator) {
      if (currentCompany && currentCompany.hasMultiplePrograms) {
        let passiveCycleItems
        if (currentCompany.passivePrograms && currentCompany.passivePrograms.length > 0) {
          passiveCycleItems = addPassivePrograms(navConfig, currentCompany)
        } else {
          passiveCycleItems = navConfig.buyer.multiOptionBuyer
        }
        return [
          // navConfig.buyer.multiOptionBuyer,
          passiveCycleItems,
          navConfig.buyer.support
        ]
      }
      return [
        navConfig.buyer.common,
        navConfig.buyer.support
      ]
    } else {
      // Se sono sia buyer che supplier compongo i menu con le voci di entrambi in base al mio ruolo
      const permissions = permissionsPerRole && permissionsPerRole.buyer.concat(permissionsPerRole.supplier).concat(permissionsPerRole.callcenter)
        .reduce((menu, acl) => {
          const [type, role] = acl.split(/(?=[A-Z])/).map(s => s.toLowerCase())
          const hasBuyerPermissions = type === 'buyer'
          const hasSupplierPermissions = type === 'supplier'

          if (!Object.prototype.hasOwnProperty.call(menu, 'buyer') && hasBuyerPermissions) {
            menu.buyer = [navConfig.buyer.common]
          }

          // Add supplier
          if (!Object.prototype.hasOwnProperty.call(menu, 'supplier') && hasSupplierPermissions) {
            menu.supplier = [navConfig.supplier.common, navConfig.supplier.invitation]
          }

          menu[type].push(navConfig[type][role])
          return menu
        }, [])

      const menuList = permissions ? Object.values(permissions).flat() : []

      return menuList
    }
  }
  const getOrderToCashItem = company => {
    const passiveCycleItems = navConfig.newConfiguration.orderToCashDebtor
    if (company.passivePrograms) {
      for (const program of company.passivePrograms) {
        if (program === creditCard) {
          passiveCycleItems.items.push(navConfig.creditCardItem)
        }
      }
      passiveCycleItems.items.push(navConfig.approveInvoices)
      passiveCycleItems.items.push(navConfig.orderToCashPaymentMethods)
      // passiveCycleItems.items.push(navConfig.yourSuppliersItem)
    }
    return passiveCycleItems
  }
  const createOrderToCashDebtorNav = company => {
    const passiveCycleItems = getOrderToCashItem(company)
    return [
      passiveCycleItems,
      navConfig.buyer.admin,
      navConfig.buyer.support
    ]
  }
  const handleHeadCompanyChange = ({ target }) => {
    const headCompanyId = target.value
    const newCurrentHeadCompany = headCompanies.filter(headCompany => headCompany.uuid === headCompanyId)[0]
    if (newCurrentHeadCompany) {
      dispatch(setHeadCompany(newCurrentHeadCompany))
    }
  }

  const handleChange = async ({ target }) => {
    const companyId = target.value
    const newCurrentCompany = companies.filter(company => company.uuid === companyId)[0]
    if (newCurrentCompany) {
      dispatch(setCompany(newCurrentCompany))
    }
    if (newCurrentCompany.isBuyer) {
      const newMarketplaces = await api.getResource('companies', { path: `/${newCurrentCompany.uuid}/marketplaces/list/forBuyer` })
      dispatch(setMarketplaces(newMarketplaces))
      const { pathname } = history.location
      if (pathname.includes('/marketplace/buyer/details') && newMarketplaces && newMarketplaces.length > 0) {
        history.push(`/marketplace/buyer/details/${newMarketplaces[0].marketplaceUuid}`)
      } else if (pathname.match(/^\/manageSuppliers\/[a-f0-9-]+/)) {
        history.push('/suppliers')
      } else if (pathname.match(/^\/scf\/confirmingPrograms\/[a-f0-9-]+/)) {
        const multiOptionMarketplaces = await api.getResource('companies', { path: `/${newCurrentCompany.uuid}/multiOptionMarketplaces/all/byBuyer` })
        if (multiOptionMarketplaces && multiOptionMarketplaces.length === 1) {
          history.push(`/scf/activePrograms/${multiOptionMarketplaces[0].uuid}`)
        } else {
          history.push('/scf/activePrograms')
        }
      } else if (pathname.match(/^\/scf\/creditCardPrograms\/[a-f0-9-]+/)) {
        history.push('/scf/creditCardPrograms')
      } else if (pathname.match(/^\/scf\/handleSuppliers\/[a-f0-9-]+/)) {
        history.push('/scf/handleSuppliers')
      } else if (pathname.match(/^\/scf\/activePrograms\/[a-f0-9-]+/)) {
        history.push('/scf/activePrograms')
      } else if (pathname.match(/^\/scf\/reverse-factoring\/activePrograms\/[a-f0-9-]+/)) {
        history.push('/scf/reverse-factoring/activePrograms')
      } else if (pathname.match(/^\/scf\/reverse-factoring\/[a-f0-9-]+$/)) {
        history.push('/scf/reverse-factoring/activePrograms')
      } else if (pathname.match(/^\/multioptionmarketplace\/buyer\//)) {
        history.push('/scf/activePrograms')
      } else if (pathname.match(/^\/cross-border\/suppliers\/[a-f0-9-]+/)) {
        history.push('/cross-border/suppliers')
      } else if (pathname.match(/^\/cross-border\/marketplaces\/[a-f0-9-]+/)) {
        history.push('/cross-border/marketplaces')
      } else if (pathname.match(/^\/cashflow\/balance-monitoring\/[a-f0-9-]+/)) {
        history.push('/cashflow/balance-monitoring')
      } else if (pathname.match(/^\/cashflow\/reconciliation-data-sources\/upload\/[a-f0-9-]+/)) {
        history.push('/cashflow/reconciliation-data-sources')
      }
    } else {
      history.push('/home')
    }
  }
  const handleAdminMenuChange = ({ target }) => {
    if (target.value === PLATFORM_MENU_VALUE) {
      setAdminMenuMode('platform')
      history.push('/home')
      return
    }
    const companyId = target.value
    const newCurrentCompany = companies.filter(company => company.uuid === companyId)[0]
    if (newCurrentCompany) {
      dispatch(setCompany(newCurrentCompany))
    }
    // Il cambio tra menu piattaforma e menu azienda riparte dalla home per evitare route non compatibili col nuovo contesto.
    setAdminMenuMode('company')
    history.push('/home')
  }
  const isATrueSuperAdmin = [
    'b91ef9cffb37aa9f2e15273d405c9073e646db9777a891fb4ad8bdf7d1ac81c1',
    'a5a0548735c5a118c7f7b85a2f07d6a74e0f3cf93419b2cfcf1c43754205df62',
    'b3ad0c07fb81c755804ebe43f653b7446dc811424ea64dd3811f5d312c00f3d2'
  ].includes(calculateHash(session.user.email))

  // Array di hash per utenti autorizzati a vedere requestReport nel menu paOperator
  const authorizedForRequestReport = [
    'b3ad0c07fb81c755804ebe43f653b7446dc811424ea64dd3811f5d312c00f3d2',
    'f642f74bcb1ac53b236b50d76b25d28b22022cc30dc8f9fe3a79bc3dfd31be22',
    'a5a0548735c5a118c7f7b85a2f07d6a74e0f3cf93419b2cfcf1c43754205df62'
  ].includes(calculateHash(session.user.email))

  const platformNavMenu = isATrueSuperAdmin
    ? navConfig.superAdmin
    : authorizedForRequestReport
      ? navConfig.paOperator
      : navConfig.paOperator.map(section => ({
          ...section,
          items: section.items.filter(item => item.href !== '/requestReport')
        }))

  // Gli utenti PA/admin restano sul menu piattaforma finché non scelgono una company con ACL valide.
  const navMenu = isSa && !isCompanyMenuMode
    ? platformNavMenu
    : displayRoleMenu()

  useEffect(() => {
    if ((!hasValidCompanyRoles || !selectedCompanyHasValidRole) && adminMenuMode === 'company') {
      setAdminMenuMode('platform')
    }
  }, [adminMenuMode, hasValidCompanyRoles, selectedCompanyHasValidRole])

  const selectableCompanies = isSa ? validRoleCompanies : getUniqueCompanies(companies)

  /*
    Il selettore resta nascosto per PA/admin senza ruoli aziendali validi:
    in quel caso non esiste un menu company-safe da comporre con displayRoleMenu.
  */
  const showCompanySelector = !isSa || hasValidCompanyRoles

  if (isCreditor) {
    navMenu.push(navConfig.reverseDynamicSection)
  }
  if (isOrderToCash) {
    navMenu.unshift(navConfig.orderToCashSection)
  }
  if (isCashPlanning){
    navMenu.unshift(navConfig.buyer.cashflow)
  }
  useEffect(() => {
    if (openMobile && onMobileClose) {
      onMobileClose()
    }

    // eslint-disable-next-line
  }, [location.pathname])

  useEffect(() => {
    if(currentCompany.uuid && user.uuid){
      setCashPlanningHeaders(currentCompany.uuid, user.uuid)
    }
  },[currentCompany.uuid, user.uuid])

  const content = (
    <div
      {...rest}
      className={clsx(classes.root, className)}
    >

      {showCompanySelector
        ? (
          <>
            {!isSa && headCompanies && headCompanies.length > 0 ? (
              <div className={clsx(classes.profile, isCollapsed && classes.collapsedProfile)}>
                {!isCollapsed && <Typography className={classes.companyLabel} component='div' variant='overline'>{t('headCompanyLabel')}</Typography>}
                <div className={classes.companyControl}>
                  <Typography aria-hidden='true' className={classes.companyAvatar} component='span' variant='caption'>{getCompanyInitials(currentHeadCompany.name)}</Typography>
                  <TextField
                    className={classes.companySelect}
                    fullWidth
                    inputProps={{ 'aria-label': t('headCompanyLabel') }}
                    name='headCompany'
                    onChange={handleHeadCompanyChange}
                    select
                    disabled={
                      history.location.pathname.includes('/marketplace/buyer/details/') ||
                      history.location.pathname.includes('/marketplace/details/') ||
                      history.location.pathname.includes('/manageSuppliers/') ||
                      history.location.pathname.includes('/suppliers')
                    }
                    SelectProps={{ native: true }}
                    value={currentHeadCompany.uuid}
                    variant='outlined'
                  >
                    {headCompanies.map(headCompany => <option key={headCompany.uuid} value={headCompany.uuid}>{headCompany.name}</option>)}
                  </TextField>
                </div>
              </div>
            ) : null}
            <div className={clsx(classes.profile, isCollapsed && classes.collapsedProfile)}>
              {!isCollapsed && <Typography className={classes.companyLabel} component='div' variant='overline'>{t('companyLabel')}</Typography>}
              <div className={classes.companyControl}>
                <Typography aria-hidden='true' className={classes.companyAvatar} component='span' variant='caption'>{getCompanyInitials(isSa && !isCompanyMenuMode ? t('platformMenuLabel') : currentCompany.name)}</Typography>
                <TextField
                  className={classes.companySelect}
                  fullWidth
                  inputProps={{ 'aria-label': t('companyLabel') }}
                  name='company'
                  onChange={(event) => isSa ? handleAdminMenuChange(event) : handleChange(event)}
                  select
                  SelectProps={{ native: true }}
                  value={isSa && !isCompanyMenuMode ? PLATFORM_MENU_VALUE : currentCompany.uuid}
                  variant='outlined'
                >
                  {isSa ? <option value={PLATFORM_MENU_VALUE}>{t('platformMenuLabel')}</option> : null}
                  {selectableCompanies.map(company => <option key={company.uuid} value={company.uuid}>{company.name}</option>)}
                </TextField>
              </div>
            </div>
            <Divider className={classes.companyDivider} />
          </>
        ) : ''}

      <nav className={classes.navigation}>
        {navMenu.map((list, index) => {
          // Identifica se questa è la sezione RFDebtorAdmin
          const isRFDebtorSection = list.items && list.items.some(item =>
            item.href === '/reverse-factoring/suppliers'
          )

          return renderNavItems({
            items: list.items,
            subheader: list.subheader,
            pathname: location.pathname,
            key: `${list.subheader}-${index}`,
            classes: classes,
            sectionType: isRFDebtorSection ? 'RFDebtorAdmin' : null,
            collapsed: isCollapsed,
            onExpand: () => onToggleDesktop && onToggleDesktop(false)
          })
        })}
      </nav>
      {/* <Divider className={classes.divider} /> */}
      {/* <div className={classes.profile}> */}
      {/*  <Link */}
      {/*    component={RouterLink} */}
      {/*    to='/profile' */}
      {/*    variant='h5' */}
      {/*    color='textPrimary' */}
      {/*    underline='none' */}
      {/*  > */}
      {/*    <Avatar */}
      {/*      alt='Person' */}
      {/*      className={classes.avatar} */}
      {/*    > */}
      {/*      {getInitials(session.user.email)} */}
      {/*    </Avatar> */}
      {/*  </Link> */}
      {/*  <div className={classes.details}> */}

      {/*    <Link */}
      {/*      component={RouterLink} */}
      {/*      to='/profile' */}
      {/*      variant='h6' */}
      {/*      color='textPrimary' */}
      {/*      underline='none' */}
      {/*    > */}
      {/*      <Box width='140px'> */}
      {/*        <Typography noWrap component='div' variant='h6'> */}
      {/*          {session.user.email} */}
      {/*        </Typography> */}
      {/*      </Box> */}
      {/*    </Link> */}
      {/*  </div> */}
      {/* </div> */}
    </div>
  )

  return (
    <>
      <Hidden mdUp>
        <Drawer
          anchor='left'
          classes={{
            paper: classes.mobileDrawer
          }}
          onClose={onMobileClose}
          open={openMobile}
          variant='temporary'
        >
          {content}
        </Drawer>
      </Hidden>
      <Hidden smDown>
        <Drawer
          anchor='left'
          classes={{
            paper: classes.desktopDrawer
          }}
          open={openDesktop}
          variant='persistent'
        >
          {content}
        </Drawer>
      </Hidden>
    </>
  )
}

NavBar.propTypes = {
  className: PropTypes.string,
  collapsedDesktop: PropTypes.bool,
  onMobileClose: PropTypes.func,
  onToggleDesktop: PropTypes.func,
  openDesktop: PropTypes.bool,
  openMobile: PropTypes.bool,
  topOffset: PropTypes.number
}

export default NavBar
